import {useEffect, useRef} from 'react';

import {markOnboardingTaskCompleteOptimistically} from 'actions/OnboardingActions';
import OnboardingConstants, {NUEModule} from 'constants/OnboardingConstants';
import {ToolCallStatus, ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {parseManageSkillResponse} from 'llm/chat-library/tool-details/ManageSkillToolCallParser';
import {ToolType} from 'llm/chat-library/tool-details/types';

/** Whether a tool call is a ManageSkill create that finished successfully. */
function isCompletedSkillCreation(toolCall: ProgressToolCall): boolean {
  if (toolCall.tool !== ToolType.ManageSkill || toolCall.status !== ToolCallStatus.COMPLETED) {
    return false;
  }
  if (toolCall.args?.action !== 'create') {
    return false;
  }
  // The agent loop persists the skill and marks the onboarding task complete on the backend; we only
  // mirror confirmed successes so the checklist doesn't tick for a failed create.
  return parseManageSkillResponse(toolCall.responseText ?? '')?.success === true;
}

/**
 * Optimistically complete the "Create a skill" AI agents onboarding task when the chat thread shows a
 * skill was created via the agent's ManageSkill tool. The backend already records completion when the
 * skill is persisted; this reflects it live on the checklist co-rendered with the chat thread without
 * waiting for the next mount-time `fetchOnboardingTasks()`.
 *
 * Each tool call is handled at most once so the action doesn't re-fire on every render or as new
 * messages stream in.
 */
export default function useCompleteSkillOnboardingFromChat(
  toolCalls: Array<ProgressToolCall> | undefined
): void {
  const handledToolCallIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!toolCalls) {
      return;
    }
    for (const toolCall of toolCalls) {
      if (handledToolCallIds.current.has(toolCall.toolId) || !isCompletedSkillCreation(toolCall)) {
        continue;
      }
      handledToolCallIds.current.add(toolCall.toolId);
      markOnboardingTaskCompleteOptimistically(NUEModule.AI_AGENTS, OnboardingConstants.CREATE_SKILL);
    }
  }, [toolCalls]);
}
